import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSearch from './useSearch';
import { tintFor } from '../../utils/tints';
import { discoFrame, DISCO_KEYFRAMES } from '../wall/wallTokens';
import { SearchGlyph, CloseGlyph, ClockGlyph } from '../chrome/headerIcons';
import { CloseFriendMark, MirrorRoom, useCloseFriend } from '../closefriends';
import '../chrome/topHeader.css';

/**
 * Search, dropped from the black top header — top-header handoff §4 (Sept 25 2026). One panel at
 * every width (the phone's full-screen sheet is retired): it hangs from the header's bottom edge,
 * slides down from translateY(-102%), and a 40% scrim covers the page below.
 *
 *   Field (the disco frame) · scopes ALL / EVENTS / PEOPLE / CLUBS (counts once there's a query) ·
 *   empty: RECENT + COMING UP NEAR {CITY} · typed: groups (3 columns for ALL on desktop, 2 for a
 *   single scope, 1 below lg; 4 per group in ALL with ALL N →) · desktop key legend.
 *
 * Keyboard: ↑/↓ walk the rows, ↵ opens the highlighted one, Esc closes. Hover and keys share one
 * highlight index. The panel is `aria-hidden` and its input out of the tab order while closed.
 */
const NO_AUTOFILL = {
  type: 'search',
  name: 'pr-q',
  autoComplete: 'off',
  autoCorrect: 'off',
  autoCapitalize: 'off',
  spellCheck: false,
  enterKeyHint: 'search',
  'data-lpignore': 'true',
  'data-1p-ignore': 'true',
  'data-form-type': 'other',
};

const RECENT_KEY = 'pr.recentSearches';
const RECENT_MAX = 6;
function readRecent() {
  try { const v = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); return Array.isArray(v) ? v.filter((s) => typeof s === 'string') : []; } catch { return []; }
}
function pushRecent(term) {
  const t = (term || '').trim();
  if (!t) return;
  try {
    const next = [t, ...readRecent().filter((s) => s.toLowerCase() !== t.toLowerCase())].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch { /* storage blocked — recent is a convenience */ }
}

const SCOPES = [['all', 'ALL'], ['events', 'EVENTS'], ['people', 'PEOPLE'], ['clubs', 'CLUBS']];

/** The title with the matched substring in the hit highlight. */
function Hit({ text, term }) {
  const s = text || '';
  const i = term ? s.toLowerCase().indexOf(term.toLowerCase()) : -1;
  if (i < 0) return s;
  return (
    <>
      {s.slice(0, i)}
      <mark style={{ background: '#FFCCE6', color: '#000', borderRadius: 4, padding: '0 1px' }}>{s.slice(i, i + term.length)}</mark>
      {s.slice(i + term.length)}
    </>
  );
}

function Thumb({ item }) {
  const person = item.kind === 'person';
  return (
    <span
      className="flex items-center justify-center overflow-hidden font-mono text-[16px] font-bold text-black"
      style={{ width: 48, height: 48, borderRadius: person ? 999 : 12, background: item.imageUrl ? '#1F1F1F' : tintFor(item.id || item.title) }}
    >
      {item.imageUrl
        ? <img src={item.imageUrl} alt="" className="block h-full w-full object-cover" referrerPolicy="no-referrer" />
        : (item.title || '?').charAt(0).toUpperCase()}
    </span>
  );
}

function ResultRow({ item, term, on, onHover, onOpen }) {
  const person = item.kind === 'person';
  const friend = person && item.isFriend;
  const close = useCloseFriend(item.id, item.isClose, item.title);
  const price = item.kind === 'event' ? (item.tag || (item.action === 'JOIN' ? 'FREE' : null)) : null;
  return (
    <div className="relative overflow-hidden rounded-[18px]" onMouseEnter={onHover}>
      <button type="button" onClick={onOpen} className="th-row" data-on={on ? 'true' : 'false'} data-testid="search-row">
        <Thumb item={item} />
        <span className="flex min-w-0 flex-col" style={{ gap: 4 }}>
          <span className="truncate text-[14px] font-semibold" style={{ lineHeight: 1.25 }}><Hit text={item.title} term={term} /></span>
          {close.error
            ? <span role="alert" className="truncate text-[10px] font-bold uppercase tracking-[0.1em]">{close.error}</span>
            : <span className="th-row-meta truncate text-[10px] font-bold uppercase tracking-[0.1em]">{item.meta}</span>}
        </span>
        {price ? (
          <span className="th-price flex items-center whitespace-nowrap rounded-full text-[11px] font-bold" style={{ height: 26, padding: '0 10px' }}>{price}</span>
        ) : friend ? <span style={{ width: 28 }} aria-hidden /> : <span />}
      </button>
      {/* A friend's row ends in the close-friends mark; its mirror room covers the row. */}
      {friend && (
        <span className="absolute top-1/2 -translate-y-1/2" style={{ right: 12 }}>
          <CloseFriendMark size={28} on={close.on} onToggle={close.toggle} name={item.title} inverted={!on} />
        </span>
      )}
      {friend && <MirrorRoom q={close.q} surface="search" first={close.first} />}
    </div>
  );
}

export default function SearchPanel({ open, onClose, phone = false, desktop = false, inputRef: inputRefProp }) {
  const navigate = useNavigate();
  const ownRef = useRef(null);
  const inputRef = inputRefProp || ownRef;
  const [scope, setScope] = useState('all');
  const [recent, setRecent] = useState(readRecent);
  const {
    query, setQuery, sections, rows, city, counts, browse, highlight, setHighlight, term, location, isEmpty,
  } = useSearch(open, { scope, perSection: 4 });

  // Focus ~60ms after open, once the slide has started; on close, drop focus and the scope.
  useEffect(() => {
    if (!open) { setScope('all'); return undefined; }
    setRecent(readRecent());
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [open, inputRef]);

  const close = useCallback(() => { inputRef.current?.blur(); onClose(); }, [inputRef, onClose]);

  const go = useCallback((row) => {
    if (!row) return;
    pushRecent(term);
    close();
    navigate(row.type === 'city' ? row.city.href : row.item.href);
  }, [navigate, close, term]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, Math.max(navRows.length - 1, 0))); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (navRows[highlight]) go(navRows[highlight]);
      else if (term) { pushRecent(term); close(); navigate(`/search?q=${encodeURIComponent(term)}`); }
    }
  };

  const pickScope = (k) => { setScope(k); setHighlight(0); inputRef.current?.focus(); };

  // Empty query in ALL: RECENT chips + three events coming up near you.
  const browsing = !term && scope === 'all';
  const cityName = location?.label && location.label !== 'Your location' ? location.label : null;
  const upcoming = (browse?.events?.items || []).slice(0, 3);
  const groupDefs = browsing
    ? (upcoming.length ? [{ key: 'events', label: `COMING UP NEAR ${(cityName || 'YOU').toUpperCase()}`, items: upcoming }] : [])
    : sections.map((s) => ({
      key: s.key,
      label: !term && scope !== 'all'
        ? (s.key === 'people' ? 'YOUR PEOPLE' : s.key === 'clubs' ? 'CLUBS NEAR YOU' : 'COMING UP')
        : s.label.toUpperCase(),
      items: s.items,
      total: s.serverTotal,
    }));
  // In browse mode the rows the keyboard walks are the upcoming three.
  const navRows = browsing ? upcoming.map((item) => ({ type: 'item', section: 'events', item })) : rows;
  const cols = desktop && term && scope === 'all' ? 'repeat(3, minmax(0,1fr))' : (desktop && scope !== 'all' ? 'repeat(2, minmax(0,1fr))' : 'minmax(0,1fr)');
  const noResults = !!term && isEmpty;

  let cursor = city && !browsing ? 0 : -1;

  return (
    <>
      <style>{DISCO_KEYFRAMES}</style>
      <div className="th-scrim" onClick={close} aria-hidden="true" style={{ opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none' }} />
      <div className="th-panel-clip" style={{ pointerEvents: open ? 'auto' : 'none' }} aria-hidden={open ? undefined : 'true'} data-testid="search-panel" data-open={open ? 'true' : 'false'} {...(!open ? { inert: '' } : {})}>
        <div className="th-panel" style={{ transform: open ? 'translateY(0)' : 'translateY(-102%)', padding: phone ? '8px 16px 20px' : '12px 28px 24px' }}>
          <div className="mx-auto flex flex-col" style={{ maxWidth: 1080, gap: phone ? 14 : 18 }}>

            <label className="flex items-center" data-disco="" data-th-motion="" style={{ ...discoFrame({ fill: '#000' }), height: phone ? 48 : 52, gap: 10, padding: '0 8px 0 18px', borderRadius: 999, boxSizing: 'border-box' }}>
              <SearchGlyph size={18} color="#fff" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={phone ? 'SEARCH PORTAL' : 'SEARCH EVENTS, PEOPLE, CLUBS'}
                aria-label="Search"
                tabIndex={open ? 0 : -1}
                {...NO_AUTOFILL}
                className="th-input font-mono"
                style={{ fontSize: phone ? 16 : 14 }}
              />
              {query && (
                <button type="button" onClick={() => { setQuery(''); inputRef.current?.focus(); }} aria-label="Clear" tabIndex={open ? 0 : -1}
                  className="flex flex-shrink-0 items-center justify-center rounded-full text-white" style={{ width: 32, height: 32, background: '#1F1F1F' }}>
                  <CloseGlyph size={12} width={3} />
                </button>
              )}
              {!phone && (
                <button type="button" onClick={close} tabIndex={open ? 0 : -1}
                  className="flex-shrink-0 rounded-full font-mono text-[10px] font-bold tracking-[0.14em]" style={{ height: 32, padding: '0 12px', background: '#1F1F1F', color: '#BDBDBD' }}>
                  ESC
                </button>
              )}
            </label>

            <div className="th-noscroll flex overflow-x-auto" style={{ gap: 8 }} role="group" aria-label="Search in">
              {SCOPES.map(([k, l]) => (
                <button key={k} type="button" onClick={() => pickScope(k)} aria-pressed={scope === k} tabIndex={open ? 0 : -1} className="th-chip">
                  {l}
                  {term && k !== 'all' && <span style={{ fontWeight: 400, color: '#9CA3AF' }}>{counts[k]}</span>}
                </button>
              ))}
            </div>

            {browsing && recent.length > 0 && (
              <div className="flex flex-col" style={{ gap: 10 }}>
                <span className="text-[10px] font-bold tracking-[0.14em]" style={{ color: '#8A8A8A' }}>RECENT</span>
                <div className="flex flex-wrap" style={{ gap: 8 }}>
                  {recent.map((t) => (
                    <button key={t} type="button" tabIndex={open ? 0 : -1} onClick={() => { setQuery(t); inputRef.current?.focus(); }}
                      className="th-recent flex items-center rounded-full font-mono text-[12px] text-white" style={{ gap: 8, height: 32, padding: '0 14px 0 12px', background: '#161616' }}>
                      <ClockGlyph />{t}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {(groupDefs.length > 0 || (city && !browsing)) && (
              <div className="th-scroll overflow-y-auto" style={{ maxHeight: phone ? '52vh' : 420, margin: '0 -8px', padding: '0 8px' }}>
                {city && !browsing && (
                  <div onMouseEnter={() => setHighlight(0)} style={{ marginBottom: 8 }}>
                    <button type="button" className="th-row" data-on={highlight === 0 ? 'true' : 'false'} onClick={() => go(rows[0])} tabIndex={open ? 0 : -1}>
                      <span className="flex items-center justify-center rounded-full" style={{ width: 48, height: 48, background: '#1F1F1F', color: '#fff' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></svg>
                      </span>
                      <span className="flex min-w-0 flex-col" style={{ gap: 4 }}>
                        <span className="truncate text-[14px] font-semibold">See all events in {city.name}</span>
                        <span className="th-row-meta truncate text-[10px] font-bold uppercase tracking-[0.1em]">
                          {city.eventCount} {city.eventCount === 1 ? 'event' : 'events'} · everything below is in {city.name}
                        </span>
                      </span>
                      <span className="th-price flex items-center rounded-full text-[11px] font-bold" style={{ height: 26, padding: '0 10px' }}>CITY</span>
                    </button>
                  </div>
                )}
                <div className="grid" style={{ gridTemplateColumns: cols, gap: desktop ? 20 : 14 }}>
                  {groupDefs.map((g) => (
                    <div key={g.key} className="flex min-w-0 flex-col" style={{ gap: 2 }}>
                      <div className="flex items-baseline justify-between" style={{ gap: 8, padding: '0 8px 8px' }}>
                        <span className="text-[10px] font-bold tracking-[0.14em]" style={{ color: '#8A8A8A' }}>{g.label}</span>
                        {scope === 'all' && term && g.total > g.items.length && (
                          <button type="button" tabIndex={open ? 0 : -1} onClick={() => pickScope(g.key)} className="font-mono text-[10px] font-bold tracking-[0.1em] text-white">
                            ALL {g.total} →
                          </button>
                        )}
                      </div>
                      {g.items.map((item) => {
                        cursor += 1;
                        const idx = cursor;
                        return (
                          <ResultRow
                            key={`${g.key}-${item.id}`}
                            item={item}
                            term={term}
                            on={idx === highlight}
                            onHover={() => { if (highlight !== idx) setHighlight(idx); }}
                            onOpen={() => go(navRows[idx])}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {noResults && (
              <div className="flex flex-col" style={{ padding: '20px 8px 8px', gap: 6 }}>
                <span className="text-[12px] font-bold tracking-[0.1em]">NO MATCHES FOR “{term.toUpperCase()}”.</span>
                <span className="text-[12px]" style={{ color: '#9CA3AF' }}>Try a venue, a city or a first name.</span>
              </div>
            )}

            {desktop && (
              <div className="flex items-center text-[10px] tracking-[0.1em]" style={{ gap: 16, padding: '4px 8px 0', color: '#8A8A8A' }} aria-hidden="true">
                {[['↑↓', 'MOVE'], ['↵', 'OPEN'], ['ESC', 'CLOSE']].map(([k, l]) => (
                  <span key={l} className="flex items-center" style={{ gap: 6 }}><Key>{k}</Key>{l}</span>
                ))}
                <span className="flex-1" />
                <span className="flex items-center" style={{ gap: 6 }}><Key>/</Key>FROM ANYWHERE</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

const Key = ({ children }) => (
  <span className="rounded-full font-bold text-white" style={{ padding: '2px 7px', background: '#1F1F1F' }}>{children}</span>
);
